import { CustomAuthorizerEvent } from 'aws-lambda';
import { decode, verify, VerifyOptions } from 'jsonwebtoken';
import jwksClient from 'jwks-rsa';
import { promisify } from 'util';
import { isLocal } from './environment';
import errorWithCode from './error-with-code';

const auth0Domain = 'development-please.eu.auth0.com';
const audience = 'https://development-please.eu.auth0.com/api/v2/';

const exceptionalResourceURLs = [
  '/trips/{tripId}',
  '/trips/get-ids',
  '/trips/containing-service/{serviceId}',
  '/trips/{tripId}/availability',
  '/trips/{tripId}/copy',
  '/services/{serviceId}',
];

const authClient = jwksClient({
  cache: true,
  jwksUri: `https://${auth0Domain}/.well-known/jwks.json`,
});

const tokenOpts = {
  algorithms: ['RS256'],
  audience,
  // issuer: issuer
};

// extract and return the Bearer Token from the Lambda event parameters
const retrieveToken = (event: CustomAuthorizerEvent) => {
  const tokenString = event.headers && event.headers.Authorization;
  if (!tokenString) {
    throw errorWithCode(401, 'Authorization header not passed', { report: false });
  }
  const match = tokenString.match(/^Bearer (.*)$/);
  if (!match || match.length < 2) {
    throw errorWithCode(
      401,
      `Invalid Authorization token - ${tokenString} does not match "Bearer .*"`,
    );
  }
  const token = match[1];
  if (!token) {
    throw errorWithCode(401, 'Unauthorized', { report: false });
  }
  return token;
};

// Validate Token is not malformed
const decodeToken = (authToken: string) => {
  const decodedJwtToken = decode(authToken, { complete: true });
  if (!decodedJwtToken) {
    throw new Error('jwt token malformed');
  }
  return decodedJwtToken;
};

// Verify Token
const verifyToken = (authToken: string, publicKey: string, opts: VerifyOptions) => {
  const verifiedToken = verify(authToken, publicKey, opts);
  return verifiedToken;
};

// Generate an IAM policy that will allow invocation of a Lambda function
// tslint:disable-next-line variable-name
function generatePolicy(principalId: string, _effect: string, _resource: string) {
  const authResponse = {
    // set User ID
    principalId,
    // optionally add additional context for next function to consume
    context: {
      stringKey: 'stringval',
      numberKey: 123,
      booleanKey: true,
    },
  };
  return authResponse;
}

const overloadAuthParams = async (event: CustomAuthorizerEvent) => {
  if (!isLocal) {
    throw errorWithCode(500, 'You are running local custom authorizer from non local stage');
  }
  try {
    const authToken = retrieveToken(event);
    const decodedToken = decodeToken(authToken);
    const {
      // @ts-ignore
      header: { kid },
    } = decodedToken;
    const getSigningKey = promisify(authClient.getSigningKey);
    const key = await getSigningKey(kid);
    const publicKey = key.publicKey || key.rsaPublicKey;
    if (!publicKey) {
      throw new Error('publicKey not found');
    }
    const decoded = verifyToken(authToken, publicKey, tokenOpts);
    // @ts-ignore
    const IAMPolicy = generatePolicy(decoded.sub, 'Allow', event.methodArn);
    // @ts-ignore
    event.requestContext.authorizer.principalId = IAMPolicy.principalId;
    return event;
  } catch (error) {
    // NOTE: for URLs like /services/{serviceId} & /trips/{tripId}
    // the authToken won't be there always
    // when it's there we want the normal flow to continue
    // but when it's not there we make exception for those URLs and let them through
    if (event.resource && exceptionalResourceURLs.includes(event.resource)) {
      // @ts-ignore
      event.requestContext.authorizer.principalId = undefined;
      return event;
    }
    throw error;
  }
};

export default overloadAuthParams;
